/**
 * The whole trail of one lead, drawn down a spine.
 *
 * Four things used to be needed to answer "what happened with this one":
 * the pipeline row for where it sits, the activity list for the calls, the
 * Follow-ups page for the rounds after the demo, and Clients for whether
 * they ever paid. This is those four in one column, oldest at the top,
 * because that is the order somebody tells the story in.
 *
 * ## Two questions, in the order they are asked
 *
 * It was one undifferentiated list of lines, every one the same weight, and
 * reading it told you nothing about where you were: the arrival, a note, a
 * stage move and a call due tomorrow all looked identical, and the only
 * marker of "now" was that the page stopped. Nobody could see what had
 * happened, and nobody could see what was owed.
 *
 * So it is two sections with their names on them — **what happened**, then
 * **what is next** — with a line across the middle saying so. The steps are
 * numbered, because a trail is a sequence and the number is what somebody
 * points at when they talk about it. The most recent carries a badge, so the
 * end of the story is found without counting.
 *
 * ## Working from it, not just reading it
 *
 * The trail is where the work is read, so it is where the work is done: a
 * logged call carries Edit, and one still open carries a tick as well. The
 * other lines — the arrival, a stage move, the demo — are records of things
 * that happened and have nothing to change.
 *
 * What is still owed sits underneath rather than in the trail: a task nobody
 * has done has not happened, and mixing the two made a plan read as a fact.
 * Only one may be open at a time; see openWork().
 */
import { Fragment } from 'react';
import Icon from '../lib/icons.jsx';
import { useApp } from '../store/AppContext.jsx';
import { journeyOf, kindMeta, openWork } from '../domain/journey.js';
import { daysAgo } from '../lib/dates.js';

/**
 * "today" · "3d ago" · "tomorrow" · "in 5d".
 *
 * It printed `${daysAgo(at)}d ago` unconditionally, so a call booked for
 * tomorrow read "-1d ago" — which is not a date anybody can parse at a
 * glance, and it was sitting on the one line of the trail that is about the
 * future. Something dated ahead has not happened yet, and the trail should
 * say that in the same words a person would.
 */
function when(at) {
  const d = daysAgo(at);
  if (d === 0) return 'today';
  if (d === 1) return 'yesterday';
  if (d > 1) return `${d}d ago`;
  if (d === -1) return 'tomorrow';
  return `in ${Math.abs(d)}d`;
}

export default function DealJourney({ deal, booking, limit = 0, onLog, onEdit }) {
  const { S, crm, rows, demos, fmtDate, saveActivity } = useApp();
  const { events, due, deal: onDeal } = journeyOf({ crm, rows, demos, deal, booking, S });

  /*
   * Ticking one off from here rather than through the form: the only thing
   * most of them need is to be marked done, and having to open a
   * seven-field sheet to change one checkbox is why they stay open.
   */
  const finish = act => saveActivity(act.id, { ...act, done: true });

  const open = onDeal ? openWork(crm, onDeal) : null;

  if (!events.length) {
    return (
      <p className="vnone">
        Nothing has been recorded against this one yet — not even the day it arrived.
      </p>
    );
  }

  /* Long trails are cut from the front, not the back: the recent end is the
     one being worked, and "+9 earlier" is a truer thing to hide than the
     last three rounds of chasing. */
  const hidden = limit && events.length > limit ? events.length - limit : 0;
  const shown = hidden ? events.slice(hidden) : events;

  return (
    <div className="djwrap">
      <div className="djsec">
        <span className="djsec-n">1</span>
        <h5>What happened</h5>
        <small>{events.length} {events.length === 1 ? 'step' : 'steps'}, oldest first</small>
      </div>

      {hidden ? (
        <p className="djmore">
          <Icon name="clock" />
          {hidden} earlier {hidden === 1 ? 'step' : 'steps'} — the oldest is{' '}
          {fmtDate(events[0].at)}.
        </p>
      ) : null}

      <ol className="djtrail">
        {shown.map((e, i) => {
          const [icon, tone, label] = kindMeta(e.kind);
          /* Numbered across the whole trail, not across what is on screen:
             hiding the first nine must not renumber the tenth to 1. */
          const step = hidden + i + 1;
          const last = i === shown.length - 1;
          /*
           * Where the chase changed hands.
           *
           * One relationship, worked on two boards: chased as a lead, then
           * chased again as a deal. Both belong on one trail — splitting
           * them into two panels would hide the fact that it is the same
           * customer — but a call made before the promotion and one made
           * after are not the same kind of fact, and the list read as
           * though they were.
           *
           * So a band is drawn where the board changes, and only there.
           * Marking every line would put a chip on forty rows to say
           * something that changes twice. Lines that belong to no board —
           * the arrival, the demo, the booking — do not break the run: they
           * happened to the record rather than on either board.
           */
          const boardOf = x => x.board || '';
          const prev = shown.slice(0, i).reverse().find(x => boardOf(x));
          const band = boardOf(e) && boardOf(e) !== (prev ? boardOf(prev) : '')
            ? boardOf(e) : '';
          return (
            <Fragment key={e.act?.id || `e${i}`}>
              {band ? (
                <li className={`djband ${band}`} aria-hidden="false">
                  <span className="djband-line" />
                  <span className="djband-txt">
                    <Icon name={band === 'pipeline' ? 'zap' : 'card'} />
                    {band === 'pipeline' ? 'Logged on the pipeline' : 'Logged on Deals'}
                  </span>
                  <span className="djband-line" />
                </li>
              ) : null}
            <li className={`djev k-${e.kind}${last ? ' latest' : ''}`}>
              <span className="djstep" aria-hidden="true">{step}</span>
              <span className={`djdot ic ${tone}`}><Icon name={icon} /></span>
              <div className="djbody">
                <div className="djhead">
                  <span className={`djkind ${tone}`}>{label}</span>
                  <b>{e.title}</b>
                  {/* Recorded more than once on the day. Said, not hidden:
                      a duplicated stage move is usually somebody clicking
                      twice, and that is worth being able to see. */}
                  {e.times > 1 ? <span className="djtimes">×{e.times}</span> : null}
                  {/* What the same action also did. The log form moves the
                      stage as well as recording the call, and that is one
                      thing somebody did, not two. */}
                  {(e.moved || []).map(m => (
                    <span key={m} className="djmoved"><Icon name="refresh" />{m}</span>
                  ))}
                  {last ? <span className="djlatest">latest</span> : null}

                  {e.act && onEdit ? (
                    <button
                      type="button"
                      className="djbtn"
                      title="Change what was logged"
                      onClick={() => onEdit(e.act.id)}
                    >
                      <Icon name="edit" />Edit
                    </button>
                  ) : null}
                </div>
                <small className="djwhen">
                  {fmtDate(e.at)} · {when(e.at)}{e.who ? ` · ${e.who}` : ''}
                </small>
                {e.note ? <p className="djnote">{e.note}</p> : null}
              </div>
            </li>
            </Fragment>
          );
        })}
      </ol>

      {/*
        * Where the record stops and the plan starts.
        *
        * Everything above is a fact; everything below is something somebody
        * said they would do. They were one list, so a call pencilled in for
        * next week read exactly like a call that had been made.
        */}
      <div className="djsec next">
        <span className="djsec-n">2</span>
        <h5>What is next</h5>
        <small>
          {due.length
            ? `${due.length} still owed`
            : 'nothing booked — log what happens next'}
        </small>
      </div>

      {due.length ? (
        <div className="djdue">
          <ul>
            {due.slice(0, 4).map((t, i) => (
              <li key={t.act?.id || i}>
                <span className="djdot ic g-amber"><Icon name="clock" /></span>
                <div className="djduetxt">
                  <div className="djhead">
                    <span className="djkind g-amber">{t.type}</span>
                    <b>{t.title}</b>
                  </div>
                  <small className="djwhen">
                    {t.at ? `${fmtDate(t.at)} · ${when(t.at)}` : 'no date'}
                    {t.who ? ` · ${t.who}` : ''}
                  </small>
                </div>
                {/* Both buttons hang off onEdit, which is how a caller says
                    this trail is worked from and not only read. The activity
                    form shows the trail above its own fields, and a Done
                    button in there would finish one record from inside
                    another half-written one. */}
                {t.act && onEdit ? (
                  <span className="djacts">
                    <button type="button" className="djbtn" onClick={() => onEdit(t.act.id)}>
                      <Icon name="edit" />Edit
                    </button>
                    {/* Only on the ones that are open. A line already ticked
                        is in the trail above, where there is nothing left
                        to complete. */}
                    <button
                      type="button"
                      className="djbtn ok"
                      title="Mark it done — it moves up into the trail"
                      onClick={() => finish(t.act)}
                    >
                      <Icon name="check" />Done
                    </button>
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
          {due.length > 4 ? <small className="djrest">and {due.length - 4} more</small> : null}
        </div>
      ) : null}

      {/* Given by whoever is showing the trail, because the form opens in
          the same slot this panel is sitting in: the sheet has to close
          itself before it asks for another one. */}
      {onLog ? (
        <div className="djadd">
          <button type="button" className="btn primary sm" onClick={onLog} disabled={!!open}>
            <Icon name="plus" />Log what happened
          </button>
          {/* Said, rather than left to be guessed from a dead button. */}
          {open ? (
            <small className="djwait">
              Finish “{open.subject}” first — one open at a time.
            </small>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
